"use client";

import { useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import LadderRows, {
  type HintMode,
} from "@/components/tools/games-organizers/word-ladder-sheet";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import type { WORD_LADDER_GENERATOR_STRINGS } from "@/components/tools/word-ladder-generator";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Ladder,
  type LadderSearch,
  PATH_COUNT_CAP,
} from "@/lib/tool-kit/games-organizers/word-ladder";

type Strings = typeof WORD_LADDER_GENERATOR_STRINGS;
type PrintTarget = "sheet" | "both";

const EXTRA_CHOICES = [0, 1, 2, 3] as const;
const SHEET_H2 = "font-semibold text-xl";
const SHEET = "space-y-4 text-foreground print:text-black";

function NameLine({ strings }: { strings: Strings }) {
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
      <span className="flex items-end gap-2">
        {strings.name}
        <span className="inline-block w-56 border-current border-b" />
      </span>
      <span className="flex items-end gap-2">
        {strings.date}
        <span className="inline-block w-32 border-current border-b" />
      </span>
    </div>
  );
}

/** What the ladder is, how it compares with the shortest possible, and any odd words. */
function Notes({
  search,
  ladder,
  start,
  end,
  strings,
  setBannedText,
}: {
  search: LadderSearch;
  ladder: Ladder;
  start: string;
  end: string;
  strings: Strings;
  setBannedText: (update: (text: string) => string) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const stepsText = (count: number) =>
    tPlural(strings.steps, locale, count, { count: fmt(count) });
  const shortest = search.shortest;
  const countText = shortest
    ? tPlural(strings.countNote, locale, shortest.count, {
        count: fmt(shortest.count),
        steps: stepsText(shortest.steps),
      })
    : "";
  return (
    <div aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{strings.heading}</h2>
      <p className="font-medium text-foreground">
        {t(strings.summary, { start, end, steps: stepsText(ladder.steps) })}
      </p>
      {shortest && shortest.steps < ladder.steps ? (
        <p className={HINT}>
          {t(strings.shortestNote, { steps: stepsText(shortest.steps) })}
        </p>
      ) : null}
      {shortest ? (
        <p className={HINT}>
          {shortest.count >= PATH_COUNT_CAP
            ? t(strings.countCapped, {
                count: fmt(PATH_COUNT_CAP - 1),
                steps: stepsText(shortest.steps),
              })
            : countText}
        </p>
      ) : null}
      {search.unfamiliar.length > 0 ? (
        <div className="space-y-2">
          <Notice>
            {t(strings.unfamiliarNote, { words: search.unfamiliar.join(", ") })}
          </Notice>
          <button
            className={BUTTON_SMALL}
            onClick={() =>
              setBannedText((text) =>
                [text.trim(), ...search.unfamiliar].filter(Boolean).join(" ")
              )
            }
            type="button"
          >
            {strings.avoidThese}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The worksheet options, the printable worksheet and the answer key. */
export default function LadderOutput({
  search,
  ladder,
  start,
  end,
  strings,
  setBannedText,
}: {
  search: LadderSearch;
  ladder: Ladder;
  start: string;
  end: string;
  strings: Strings;
  setBannedText: (update: (text: string) => string) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [hint, setHint] = useState<HintMode>("none");
  const [extra, setExtra] = useState<(typeof EXTRA_CHOICES)[number]>(0);
  const [nameLine, setNameLine] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [printTarget, setPrintTarget] = useState<PrintTarget>("sheet");

  function print(target: PrintTarget) {
    flushSync(() => setPrintTarget(target));
    window.print();
  }

  const upper = { start: start.toUpperCase(), end: end.toUpperCase() };
  const label = t(strings.rungs, { start, end });
  const keyPrint = printTarget === "both" ? "print:block" : "print:hidden";
  return (
    <>
      <div className="space-y-3 print:hidden">
        <Notes
          end={end}
          ladder={ladder}
          search={search}
          setBannedText={setBannedText}
          start={start}
          strings={strings}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="wl-hint"
            label={strings.hintLabel}
            onChange={setHint}
            options={(["none", "mark", "fill"] as const).map((value) => ({
              value,
              label: strings.hints[value],
            }))}
            value={hint}
          />
          <SelectField
            id="wl-extra"
            label={strings.extraLabel}
            onChange={(value) =>
              setExtra(Number(value) as (typeof EXTRA_CHOICES)[number])
            }
            options={EXTRA_CHOICES.map((n) => ({
              value: String(n),
              label: fmt(n),
            }))}
            value={String(extra)}
          />
        </div>
        <Checkbox
          checked={nameLine}
          label={strings.nameLine}
          onChange={setNameLine}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_PRIMARY}
            onClick={() => print("sheet")}
            type="button"
          >
            {strings.printSheet}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => print("both")}
            type="button"
          >
            {strings.printBoth}
          </button>
          <button
            aria-pressed={showKey}
            className={BUTTON_SECONDARY}
            onClick={() => setShowKey((on) => !on)}
            type="button"
          >
            {showKey ? strings.hideKey : strings.showKey}
          </button>
        </div>
      </div>

      <section
        className={`rounded-lg border border-border p-5 print:border-0 print:p-0 ${SHEET}`}
      >
        <h2 className={SHEET_H2}>{strings.sheetTitle}</h2>
        {nameLine ? <NameLine strings={strings} /> : null}
        <p className="text-sm">{t(strings.instructions, upper)}</p>
        <p className="text-sm">
          {t(strings.goal, {
            steps: tPlural(strings.steps, locale, ladder.steps, {
              count: fmt(ladder.steps),
            }),
          })}
        </p>
        <LadderRows
          answers={false}
          extraRows={extra}
          label={label}
          ladder={ladder}
          mode={hint}
        />
      </section>

      <section
        className={`rounded-lg border border-border p-5 ${showKey ? "" : "hidden"} ${keyPrint} print:break-before-page print:border-0 print:p-0 ${SHEET}`}
      >
        <h2 className={SHEET_H2}>{t(strings.keyTitle, upper)}</h2>
        <LadderRows
          answers
          extraRows={0}
          label={label}
          ladder={ladder}
          mode="none"
        />
        <p className="text-sm">{strings.keyNote}</p>
      </section>
    </>
  );
}
